import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Activity, AlertCircle, CheckCircle2, Star, Clock, Loader2 } from "lucide-react";

export default function ManagerVelocityWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/analytics/manager-velocity").then((r) => setData(r.data)).catch(() => setData({ items: [] })).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="axia-card p-6 text-sm text-slate-500" data-testid="velocity-loading"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading team velocity…</div>;
  }
  if (!data || !data.items || data.items.length === 0) {
    return null;
  }

  const stale = data.items.filter((i) => (i.days_since_last_checkin ?? 999) > 14).length;

  return (
    <section className="axia-card p-6" data-testid="manager-velocity-widget">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-base font-semibold text-slate-900 flex items-center gap-2"><Activity className="w-4 h-4 text-slate-700" /> Team velocity</h2>
          <p className="text-xs text-slate-500 mt-0.5">{data.items.length} direct report{data.items.length === 1 ? "" : "s"} · {stale} with stale check-ins (&gt;14d)</p>
        </div>
        <Link to="/team-goals" className="text-xs text-slate-500 hover:text-slate-900">Team goals →</Link>
      </div>

      <ul className="mt-4 divide-y divide-slate-100">
        {data.items.map((r) => {
          const stale = (r.days_since_last_checkin ?? 999) > 14;
          const fresh = (r.days_since_last_checkin ?? 999) <= 7;
          return (
            <li key={r.employee_id} className="py-3 flex items-center gap-3" data-testid={`velocity-row-${r.employee_id}`}>
              <Link to={`/profile/${r.employee_id}`} className="font-medium text-slate-900 hover:underline flex-1 min-w-0 truncate text-sm">{r.name}</Link>
              <span className="text-[11px] text-slate-500 hidden md:block min-w-[120px] truncate">{r.designation}</span>
              <div className={`text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded-md border ${
                stale ? "bg-rose-50 text-rose-700 border-rose-200" : fresh ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"
              }`} title="Days since last check-in">
                <Clock className="w-3 h-3" />
                {r.days_since_last_checkin === null ? "no check-ins" : `${r.days_since_last_checkin}d ago`}
              </div>
              <div className="hidden lg:flex items-center gap-2 text-[11px] text-slate-500">
                <span className="text-emerald-700">{r.on_track}</span>
                <span className="text-amber-700">{r.at_risk}</span>
                <span className="text-rose-700">{r.blocked}</span>
              </div>
              <div className="text-[11px] text-slate-500 inline-flex items-center gap-1" title="Kudos received in last 30 days">
                <Star className="w-3 h-3 text-violet-500" /> {r.kudos_received_30d}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
